import { Component, OnInit, OnDestroy, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthService } from '../../services/auth.service';
import { firstValueFrom } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { Router, RouterModule } from '@angular/router';
import { StorageService } from '../../services/storage.service';

import { TranslateModule } from '@ngx-translate/core';
import { TranslationService } from '../../services/translation.service';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { AnimationItem } from 'lottie-web';
import { LoginAnimationService } from '../../services/login-animation.service';


@Component({
  selector: 'app-login',
  standalone: true,
  imports: [RouterModule, ReactiveFormsModule, TranslateModule, LottieComponent],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnInit, OnDestroy {
  public loginForm!: FormGroup;
  options: AnimationOptions = {
    path: '/assets/lottie/bedici_1.json',
    loop: false,
    autoplay: false,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid meet',
    },
  };
  private animationItem!: AnimationItem;

  showLogo = signal(false);
  showLoginForm = signal(false);
  isAnonymousLoading = signal(false);
  showPassword = signal(false);

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private toaster: ToastrService,
    private authService: AuthService,
    private storageService: StorageService,
    private translationService: TranslationService,
    private loginAnimationService: LoginAnimationService,
  ) {
    this.loginForm = this.fb.group({
      username: ['', Validators.required],
      password: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    this.startAnimationSequence();
  }

  ngOnDestroy(): void {
    this.loginAnimationService.resetGeometricAnimation();
  }

  private startAnimationSequence(): void {
    setTimeout(() => {
      this.showLogo.set(true);
    }, 500);
  }

  async onSubmit() {
    if (!this.loginForm.valid) {
      return;
    }

    const username = this.loginForm.value.username;
    const password = this.loginForm.value.password;

    try {
      let response = await firstValueFrom(this.authService.login(username, password));
      this.storageService.setToken(response.access_token);
      const user = await firstValueFrom(this.authService.getUserInfoOrRefreshIfNeeded());

      if (user.is_first_connection) {
        this.router.navigate(['/onboarding/1']);
      } else {
        this.router.navigate(['']);
      }
    } catch (error) {
      const errorMessage = this.translationService.instant('errors.auth.invalidCredentials');
      const errorTitle = this.translationService.instant('common.labels.error');
      this.toaster.error(errorMessage, errorTitle);
    }
  }

  async onAnonymousLogin() {
    this.isAnonymousLoading.set(true);

    try {
      const response = await firstValueFrom(this.authService.anonymousLogin());
      this.storageService.setToken(response.access_token);
      this.storageService.setAnonymous(true);
      const user = await firstValueFrom(this.authService.getUserInfoOrRefreshIfNeeded());

      if (user.is_first_connection) {
        this.router.navigate(['/onboarding/1']);
      } else {
        this.router.navigate(['']);
      }
    } catch (error) {
      const errorMessage = this.translationService.instant('errors.network.serverError');
      const errorTitle = this.translationService.instant('common.labels.error');
      this.toaster.error(errorMessage, errorTitle);
    } finally {
      this.isAnonymousLoading.set(false);
    }
  }

  togglePasswordVisibility(): void {
    this.showPassword.update((visible) => !visible);
  }

  animationCreated(animationItem: AnimationItem): void {
    this.animationItem = animationItem;
    this.animationItem.addEventListener('complete', this.onComplete.bind(this));

    // Start the animation immediately
    this.animationItem.play();
  }

  onComplete(): void {
    this.animationItem.removeEventListener('complete', this.onComplete.bind(this));

    setTimeout(() => {
      this.showLoginForm.set(true);

      setTimeout(() => {
        this.loginAnimationService.triggerGeometricAnimation();

        setTimeout(() => {
          // Ensure the animation container maintains its size and position
          this.options = {
            path: '/assets/lottie/bedici_2.json',
            autoplay: true,
            loop: true,
            rendererSettings: {
              preserveAspectRatio: 'xMidYMid meet',
            },
          };
        }, 300);
      }, 1000);
    }, 100);
  }
}
